import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Button } from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import TuneIcon from '@mui/icons-material/Tune';

/**
 * Empty state component for VirtualTable.
 * Renders outside <Table> but inside <TableContainer> with sticky positioning.
 * Stays fixed when scrolling horizontally (same behavior as loading indicator).
 * Supports context-aware messages: initial, no-data, filtered-empty, search-empty, error.
 * 
 * @component
 * @since Phase 6 - VirtualTable Enhancement Plan
 * 
*/

const TableEmptyState = ({
    type = 'no-data',
    description,
    image,
    actionText,
    onAction,
    messages = {}
}) => {
    // Default icons by context type (MUI icons)
    const defaultIcons = {
        'initial': <TuneIcon sx={{ fontSize: 60, color: 'text.disabled' }} />,
        'no-data': <InboxIcon sx={{ fontSize: 60, color: 'text.disabled' }} />,
        'filtered-empty': <FilterListOffIcon sx={{ fontSize: 60, color: 'text.disabled' }} />,
        'search-empty': <SearchOffIcon sx={{ fontSize: 60, color: 'text.disabled' }} />,
        'error': <ErrorOutlineIcon sx={{ fontSize: 80, color: 'error.light' }} />
    };

    // Default messages by context type
    const defaultMessages = {
        'initial': {
            description: messages.initial,
            icon: defaultIcons['initial']
        },
        'no-data': {
            description: messages.noData,
            icon: defaultIcons['no-data']
        },
        'filtered-empty': {
            description: messages.filteredEmpty,
            icon: defaultIcons['filtered-empty']
        },
        'search-empty': {
            description: messages.searchEmpty,
            icon: defaultIcons['search-empty']
        },
        'error': {
            description: messages.error,
            icon: defaultIcons['error']
        }
    };

    const config = defaultMessages[type] || defaultMessages['no-data'];
    
    // Renders as a div with sticky positioning (same as loading indicator)
    // Stays visible during horizontal scroll
    return (
        <Box
            className="table-empty-state"
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                padding: '48px 24px',
                backgroundColor: '#fafafa',
                // Sticky positioning to stay visible during horizontal scroll
                position: 'sticky',
                left: 0,
                width: '100%',
                boxSizing: 'border-box'
            }}
        >
            {/* Icon/Image */}
            <Box sx={{ mb: 1 }}>
            {image || config.icon}
            </Box>
            
            {/* Description text */}
            <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 300, textAlign: 'center' }}
            >
                {description || config.description}
            </Typography>
            
            {/* Action button (optional) */}
            {actionText && onAction && (
                <Button
                    variant="contained"
                    color="primary"
                    onClick={onAction}
                    sx={{ mt: 1 }}
                >
                    {actionText}
                </Button>
            )}
        </Box>
    );
};

TableEmptyState.propTypes = {
    /** Empty state context type - determines default message and icon */
    type: PropTypes.oneOf(['initial', 'no-data', 'filtered-empty', 'search-empty', 'error']),
    /** Custom description text (overrides default) */
    description: PropTypes.node,
    /** Custom image/icon component (overrides default) */
    image: PropTypes.node,
    /** Action button text */
    actionText: PropTypes.string,
    /** Action button click handler */
    onAction: PropTypes.func,
    /** Custom messages for each empty state type */
    messages: PropTypes.shape({
        initial: PropTypes.string,
        noData: PropTypes.string,
        filteredEmpty: PropTypes.string,
        searchEmpty: PropTypes.string,
        error: PropTypes.string
    })
};

TableEmptyState.displayName = 'TableEmptyState';

export default TableEmptyState;
